/**
 * W6 plan (N-E) view — the "bird's-eye" of a directional well: easting (X, east)
 * vs. northing (Y, north/up). Dependency-free SVG on the shared ChartFrame (no
 * Plotly, no external refs — CSP-safe). Both axes start at 0 (surface / wellhead
 * at origin) and extend to the plan's reach; a small compass rose marks north.
 * The path is drawn per trajectory segment so build vs. lateral read in colour.
 * Renders nothing when there is no placeable point (all-null N/E) — the caller
 * shows an empty state instead.
 */
import { useMemo } from "react";
import { ChartFrame } from "@components/chart/ChartFrame";
import type { PlanPoint, TrajectorySegment } from "./trajectoryModel";

export interface PlanViewChartProps {
  points: PlanPoint[];
  xLabel?: string;
  yLabel?: string;
  ariaLabel?: string;
}

const SEGMENT_VAR: Record<TrajectorySegment, string> = {
  vertical: "--dim",
  build: "--op-unconv",
  lateral: "--op-drilling",
};

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

function axisTicks(max: number, count: number): number[] {
  const step = max / count;
  return Array.from({ length: count + 1 }, (_, i) => Math.round(i * step * 100) / 100);
}

export function PlanViewChart({ points, xLabel, yLabel, ariaLabel }: PlanViewChartProps) {
  // Domains start at 0 (wellhead) and reach the plan's max N / E. A purely
  // vertical well has all-zero N/E; niceMax floors the domain at 1 so the single
  // origin dot still plots without a divide-by-zero.
  const eMax = useMemo(() => niceMax(Math.max(1, ...points.map((p) => p.e))), [points]);
  const nMax = useMemo(() => niceMax(Math.max(1, ...points.map((p) => p.n))), [points]);

  if (points.length === 0) return null;

  return (
    <ChartFrame
      xDomain={[0, eMax]}
      yDomain={[0, nMax]}
      width={480}
      height={300}
      xLabel={xLabel}
      yLabel={yLabel}
      ariaLabel={ariaLabel}
    >
      {({ plot, sx, sy }) => {
        const eTicks = axisTicks(eMax, 4);
        const nTicks = axisTicks(nMax, 4);
        const strokes = points.slice(1).map((p, i) => {
          const prev = points[i]!;
          return {
            key: `s${prev.seq}-${p.seq}`,
            seg: p.segment,
            line: `${sx(prev.e)},${sy(prev.n)} ${sx(p.e)},${sy(p.n)}`,
          };
        });
        return (
          <g>
            {/* N gridlines + tick labels */}
            {nTicks.map((v) => (
              <g key={`n${v}`}>
                <line
                  x1={plot.x}
                  x2={plot.x + plot.w}
                  y1={sy(v)}
                  y2={sy(v)}
                  stroke="var(--line)"
                  strokeWidth={0.5}
                  opacity={0.5}
                />
                <text
                  x={plot.x - 6}
                  y={sy(v) + 3}
                  textAnchor="end"
                  fontSize={9.5}
                  fontFamily="var(--font-mono)"
                  fill="var(--dim)"
                >
                  {Math.round(v).toLocaleString()}
                </text>
              </g>
            ))}
            {/* E gridlines + tick labels */}
            {eTicks.map((v) => (
              <g key={`e${v}`}>
                <line
                  x1={sx(v)}
                  x2={sx(v)}
                  y1={plot.y}
                  y2={plot.y + plot.h}
                  stroke="var(--line)"
                  strokeWidth={0.5}
                  opacity={0.35}
                  strokeDasharray="3,3"
                />
                <text
                  x={sx(v)}
                  y={plot.y + plot.h + 13}
                  textAnchor="middle"
                  fontSize={9}
                  fontFamily="var(--font-mono)"
                  fill="var(--dim)"
                >
                  {Math.round(v).toLocaleString()}
                </text>
              </g>
            ))}
            {/* compass rose (north up) */}
            <g transform={`translate(${plot.x + plot.w - 18}, ${plot.y + 18})`}>
              <circle r={13} fill="none" stroke="var(--line)" />
              <line x1={0} y1={-10} x2={0} y2={10} stroke="var(--dim)" strokeWidth={1} />
              <line x1={-10} y1={0} x2={10} y2={0} stroke="var(--dim)" strokeWidth={1} />
              <text
                x={0}
                y={-15}
                textAnchor="middle"
                fontSize={9}
                fontFamily="var(--font-mono)"
                fontWeight={700}
                fill="var(--head)"
              >
                N
              </text>
            </g>
            {/* per-segment trajectory strokes */}
            {strokes.map((s) => (
              <polyline
                key={s.key}
                points={s.line}
                fill="none"
                stroke={`var(${SEGMENT_VAR[s.seg]})`}
                strokeWidth={2.5}
                strokeLinecap="round"
              />
            ))}
            {/* station markers (wellhead + TD emphasised) */}
            {points.map((p, i) => {
              const isEnd = i === points.length - 1;
              const isStart = i === 0;
              return (
                <circle
                  key={`m${p.seq}`}
                  cx={sx(p.e)}
                  cy={sy(p.n)}
                  r={isEnd || isStart ? 3.5 : 2.5}
                  fill={`var(${SEGMENT_VAR[p.segment]})`}
                >
                  <title>
                    {Math.round(p.e).toLocaleString()} m E · {Math.round(p.n).toLocaleString()} m N
                  </title>
                </circle>
              );
            })}
          </g>
        );
      }}
    </ChartFrame>
  );
}
